/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { useEffect, useState, type ComponentProps } from 'react';

/* Import components */
import IntervalValuePreview from '@/components/charts/IntervalValuePreview';
import DemoSensorDeviceSelection from '@/components/tabs/demo/DemoSensorDeviceSelection';
import DemoSelectedSensorTargets from '@/components/tabs/demo/DemoSelectedSensorTargets';

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import types */
import type { IntervalValueOutputState } from '@/types/ui';

type DemoSensorDeviceSelectionProps = ComponentProps<typeof DemoSensorDeviceSelection>;

interface IntervalValueTabProps extends DemoSensorDeviceSelectionProps {
    from: string;
    hasSession: boolean;
    interval: string;
    intervalValueTarget: string;
    intervalValueTargets: string[];
    isRequestingIntervalValues: boolean;
    onAddIntervalValueTarget: (target: string) => void;
    onFromChange: (value: string) => void;
    onIntervalChange: (value: string) => void;
    onRemoveIntervalValueTarget: (target: string) => void;
    onRequestIntervalValues: () => void;
    onToChange: (value: string) => void;
    outputState: IntervalValueOutputState;
    to: string;
}

function IntervalValueTab({
    from,
    hasSession,
    interval,
    intervalValueTarget,
    intervalValueTargets,
    isRequestingIntervalValues,
    onAddIntervalValueTarget,
    onFromChange,
    onIntervalChange,
    onRemoveIntervalValueTarget,
    onRequestIntervalValues,
    onToChange,
    outputState,
    to,
    ...selectionProps
}: IntervalValueTabProps) {
    const { t } = useI18n();
    const [editableIntervalValueTarget, setEditableIntervalValueTarget] = useState(intervalValueTarget);
    const hasIntervalValueTargets = intervalValueTargets.length > 0;

    useEffect(() => {
        setEditableIntervalValueTarget(intervalValueTarget);
    }, [intervalValueTarget]);

    return (
        <div className="flex flex-col gap-6">
            <DemoSensorDeviceSelection {...selectionProps} />

            {selectionProps.selectedDevice ? (
                <>
                    <section className="dashboard-panel flex items-stretch justify-start p-5">
                        <dl className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
                            <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                                <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">{t('Customer')}</dt>
                                <dd className="mt-1 break-words text-sm text-base-content">
                                    {selectionProps.customer === 'muenster' ? t('Münster') : t('Neutraubling')}
                                </dd>
                            </div>
                            <div className="min-w-0 rounded-box bg-base-200/60 px-3 py-2">
                                <dt className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">{t('URN')}</dt>
                                <dd className="mt-1">
                                    <input
                                        id="interval-value-target"
                                        className="input input-bordered w-full font-mono text-sm"
                                        type="text"
                                        value={editableIntervalValueTarget}
                                        onChange={(event) => setEditableIntervalValueTarget(event.target.value)}
                                        spellCheck={false}
                                    />
                                </dd>
                            </div>
                        </dl>
                    </section>

                    <div>
                        <button
                            className="btn btn-secondary"
                            type="button"
                            onClick={() => onAddIntervalValueTarget(editableIntervalValueTarget)}
                            disabled={!hasSession || !editableIntervalValueTarget.trim()}
                        >
                            {t('Add sensor')}
                        </button>
                    </div>

                    <DemoSelectedSensorTargets
                        targets={intervalValueTargets}
                        onRemove={onRemoveIntervalValueTarget}
                    />

                    <section className="dashboard-panel flex items-stretch justify-start p-5">
                        <div className="grid w-full grid-cols-1 gap-y-4 text-left md:grid-cols-3 md:divide-x md:divide-base-content">
                            <div className="flex flex-col gap-2 md:pr-5">
                                <label className="text-sm font-semibold" htmlFor="interval-value-from">{t('From')}</label>
                                <input id="interval-value-from" className="input input-bordered w-full" type="text" value={from} onChange={(event) => onFromChange(event.target.value)} spellCheck={false} />
                            </div>
                            <div className="flex flex-col gap-2 md:px-5">
                                <label className="text-sm font-semibold" htmlFor="interval-value-to">{t('To')}</label>
                                <input id="interval-value-to" className="input input-bordered w-full" type="text" value={to} onChange={(event) => onToChange(event.target.value)} spellCheck={false} />
                            </div>
                            <div className="flex flex-col gap-2 md:pl-5">
                                <label className="text-sm font-semibold" htmlFor="interval-value-interval">{t('Interval')}</label>
                                <input id="interval-value-interval" className="input input-bordered w-full" type="text" value={interval} onChange={(event) => onIntervalChange(event.target.value)} placeholder="1year" spellCheck={false} />
                            </div>
                        </div>
                    </section>

                    <div>
                        <button
                            className="btn btn-primary"
                            type="button"
                            onClick={onRequestIntervalValues}
                            disabled={!hasSession || isRequestingIntervalValues || !hasIntervalValueTargets || !from.trim() || !to.trim()}
                        >
                            {isRequestingIntervalValues ? t('Requesting interval values...') : t('Request Interval Values')}
                        </button>
                    </div>

                    {outputState.outputMode === 'intervalvalues' && outputState.intervalValuePreview ? (
                        <IntervalValuePreview preview={outputState.intervalValuePreview} />
                    ) : (
                        <section className="dashboard-panel flex items-stretch justify-start p-5">
                            <div className="flex w-full flex-col gap-2">
                                <label className="text-sm font-semibold" htmlFor="interval-value-response-output">{t('Interval value output')}</label>
                                <textarea
                                    id="interval-value-response-output"
                                    className="textarea textarea-bordered response-output w-full"
                                    value={outputState.outputMode === 'raw' ? outputState.responseText : ''}
                                    readOnly
                                    spellCheck={false}
                                    placeholder={t('The raw interval value response will be shown here.')}
                                />
                            </div>
                        </section>
                    )}
                </>
            ) : null}
        </div>
    );
}

export default IntervalValueTab;
